import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { EditorDemoStore } from '../EditorDemoStore';
import { observer } from 'mobx-react-lite';
import { useDemoStore } from '../EditorDemo';

interface ReplaceWordModalProps {
  // No longer need demoStore prop
}

/**
 * A modal component for replacing transcript words
 */
const ReplaceWordModal: React.FC<ReplaceWordModalProps> = observer(() => {
  const demoStore = useDemoStore();
  const [replaceAll, setReplaceAll] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if (inputRef.current && inputRef.current.value.trim() !== '') {
      demoStore.replaceWord(inputRef.current.value, replaceAll);
      demoStore.closeReplaceWordModal();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Don't render if modal is not open
  if (!demoStore.isReplaceWordModalOpen) return null;

  return createPortal(
    <ModalOverlay>
      <ModalContainer>
        <CloseButton onClick={() => demoStore.closeReplaceWordModal()}>✕</CloseButton>
        <ModalHeader>
          <ModalTitle>Replace Word</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <TranscriptValuesContainer>
            <OriginalTextContainer>
              <OriginalTextLabel>Original:</OriginalTextLabel>
              <OriginalTextValue>{demoStore.originalWordText}</OriginalTextValue>
            </OriginalTextContainer>
            {demoStore.originalWordText !== demoStore.selectedWordText && (
              <CurrentTextContainer>
                <CurrentTextLabel>Current:</CurrentTextLabel>
                <CurrentTextValue>{demoStore.selectedWordText}</CurrentTextValue>
              </CurrentTextContainer>
            )}
          </TranscriptValuesContainer>
          
          <Label>New Text</Label>
          <Input 
            ref={inputRef} 
            type="text" 
            defaultValue={demoStore.selectedWordText || ""} 
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <CheckboxContainer>
            <Checkbox 
              type="checkbox" 
              id="replaceAll" 
              checked={replaceAll} 
              onChange={() => setReplaceAll(!replaceAll)} 
            />
            <CheckboxLabel htmlFor="replaceAll">Replace all instances</CheckboxLabel>
          </CheckboxContainer>
        </ModalContent>
        <ModalFooter>
          <CancelButton onClick={() => demoStore.closeReplaceWordModal()}>
            Cancel
          </CancelButton>
          <ReplaceButton onClick={handleSubmit}>
            Replace {replaceAll ? 'All' : ''}
          </ReplaceButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>,
    document.body
  );
});

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: #171724;
  border-radius: 8px;
  width: 400px;
  padding: 24px;
  color: #ffffff;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 18px;
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 20px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #45d483;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TranscriptValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const OriginalTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-color: #242432;
  border-radius: 4px;
  border-left: 3px solid #45d483;
`;

const CurrentTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-color: #242432;
  border-radius: 4px;
  border-left: 3px solid #3073e8;
`;

const OriginalTextLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #999;
  margin-bottom: 4px;
`;

const CurrentTextLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #999;
  margin-bottom: 4px;
`;

const OriginalTextValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  word-break: break-word;
  color: #fff;
`;

const CurrentTextValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  word-break: break-word;
  color: #fff;
`;

const ModalContent = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #333;
  background-color: #23232f;
  color: #fff;
  font-size: 14px;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #45d483;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  cursor: pointer;
  user-select: none;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1px solid #45d483;
  color: #45d483;
  &:hover {
    background: rgba(69, 212, 131, 0.1);
  }
`;

const ReplaceButton = styled(Button)`
  background: #45d483;
  border: 1px solid #45d483;
  color: #171724;
  &:hover {
    background: #3bb873;
  }
`;

export default ReplaceWordModal;